/**
 * StorageLocationsPanel — Phase 4e (Feb 2026)
 *
 * Lagerort-Verwaltung mit eindeutigem Code + druckbarem QR-Etikett.
 * Wird in der Warenwirtschafts-Section als Sub-Akkordeon eingebunden.
 *
 * Features:
 *   - Tabellen-Liste mit Code, Halle/Regal/Fach, Artikel-Count, Aktiv-Status
 *   - Inline-Anlage neuer Lagerorte
 *   - Bearbeiten via Modal
 *   - QR-Etikett-PDF download (einzeln + Bulk)
 *   - Soft-Delete (wenn Artikel zugeordnet) bzw. Hard-Delete
 */
import { useEffect, useState } from "react";
import { Printer, Plus, Trash2, Edit, FileDown, MapPin, Search } from "lucide-react";
import api from "../../lib/api";
import { previewPdfResponse } from "../../lib/pdfPreview";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { toast } from "sonner";

const EMPTY_FORM = { code: "", name: "", hall: "", rack: "", shelf: "", note: "" };

export default function StorageLocationsPanel() {
    const [items, setItems] = useState([]);
    const [siteCode, setSiteCode] = useState("");
    const [loading, setLoading] = useState(false);
    const [search, setSearch] = useState("");
    const [showCreate, setShowCreate] = useState(false);
    const [form, setForm] = useState(EMPTY_FORM);
    const [editingId, setEditingId] = useState(null);
    const [selected, setSelected] = useState(new Set());
    const [busy, setBusy] = useState(false);

    const load = async () => {
        setLoading(true);
        try {
            const r = await api.get("/dealer/storage-locations", {
                params: search ? { search } : {},
            });
            setItems(r.data || []);
        } catch (e) {
            toast.error(e.response?.data?.detail || "Lagerorte konnten nicht geladen werden");
        }
        setLoading(false);
    };

    useEffect(() => {
        load();
        // Phase 4e.B+ — Site-Code vom Profil holen für Hinweis-Anzeige
        api.get("/users/me/dealer-profile").then((r) => setSiteCode(r.data?.site_code || "")).catch(() => {});
        /* eslint-disable-next-line */
    }, []);

    const reset = () => {
        setForm(EMPTY_FORM);
        setEditingId(null);
        setShowCreate(false);
    };

    const submit = async () => {
        if (!form.code.trim()) { toast.error("Lager-Code ist Pflicht"); return; }
        setBusy(true);
        try {
            if (editingId) {
                await api.patch(`/dealer/storage-locations/${editingId}`, form);
                toast.success("Lagerort aktualisiert");
            } else {
                await api.post("/dealer/storage-locations", form);
                toast.success("Lagerort angelegt");
            }
            reset();
            await load();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Speichern fehlgeschlagen");
        }
        setBusy(false);
    };

    const startEdit = (loc) => {
        setForm({
            code: loc.code || "",
            name: loc.name || "",
            hall: loc.hall || "",
            rack: loc.rack || "",
            shelf: loc.shelf || "",
            note: loc.note || "",
        });
        setEditingId(loc.id);
        setShowCreate(true);
    };

    const remove = async (loc) => {
        const ref = loc.article_count || 0;
        const msg = ref > 0
            ? `Diesem Lagerort sind ${ref} Artikel zugeordnet. Soll der Lagerort als inaktiv markiert werden? (Artikel-Verknüpfungen bleiben bestehen.)`
            : `Lagerort '${loc.code}' wirklich löschen?`;
        if (!window.confirm(msg)) return;
        try {
            await api.delete(`/dealer/storage-locations/${loc.id}`);
            toast.success(ref > 0 ? "Lagerort deaktiviert" : "Lagerort gelöscht");
            await load();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Löschen fehlgeschlagen");
        }
    };

    const printSingle = async (loc) => {
        try {
            const r = await api.get(`/dealer/storage-locations/${loc.id}/label.pdf`, { responseType: "blob" });
            previewPdfResponse(r, `Lagerort_${loc.code}.pdf`);
        } catch (e) {
            toast.error(e.response?.data?.detail || "PDF-Generierung fehlgeschlagen");
        }
    };

    const printBulk = async () => {
        const ids = Array.from(selected);
        if (ids.length === 0) { toast.error("Bitte mindestens einen Lagerort auswählen"); return; }
        try {
            const r = await api.post("/dealer/storage-locations/labels.pdf", { ids }, { responseType: "blob" });
            previewPdfResponse(r, `Lagerort-Etiketten_${ids.length}.pdf`);
        } catch (e) {
            toast.error(e.response?.data?.detail || "Bulk-PDF fehlgeschlagen");
        }
    };

    const toggleSelect = (id) => {
        setSelected((s) => {
            const next = new Set(s);
            if (next.has(id)) next.delete(id); else next.add(id);
            return next;
        });
    };
    const selectAll = () => {
        if (selected.size === items.length) setSelected(new Set());
        else setSelected(new Set(items.map((i) => i.id)));
    };

    return (
        <div className="space-y-4" data-testid="storage-locations-panel">
            <div className="bg-blue-50 border border-blue-200 rounded-sm p-3 text-xs text-blue-900">
                <strong>Tipp:</strong> Jeder Lagerort erhält einen einzigartigen QR-Code, den Sie ausdrucken und auf das Regal/Fach kleben. Bei der Inventur scannen Sie zuerst den Artikel-Code, dann den Lagerort-QR — der Bestand wird automatisch zugeordnet.
                {siteCode ? (
                    <div className="mt-1">
                        <strong>Standort-Prefix aktiv:</strong> Neue Lagerorte werden automatisch mit <code className="font-mono-num bg-white border border-blue-300 px-1 rounded-sm">{siteCode}-</code> versehen.
                    </div>
                ) : (
                    <div className="mt-1 text-amber-800">
                        <strong>Hinweis:</strong> Kein Standort-Code gesetzt. Tragen Sie unter <em>Stammdaten → Standort-Code</em> z.B. <code className="font-mono-num bg-white border border-amber-300 px-1 rounded-sm">FBW</code> ein, um automatisch zu prefixen.
                    </div>
                )}
            </div>

            {/* Toolbar */}
            <div className="flex items-center gap-2 flex-wrap">
                <div className="relative flex-1 min-w-[200px]">
                    <Search className="w-4 h-4 absolute left-2 top-1/2 -translate-y-1/2 text-gray-400" />
                    <Input
                        type="text"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        onKeyDown={(e) => { if (e.key === "Enter") load(); }}
                        placeholder="Code oder Name suchen…"
                        className="pl-8 rounded-sm h-9"
                        data-testid="storage-locations-search-input"
                    />
                </div>
                <Button
                    type="button"
                    onClick={load}
                    variant="outline"
                    className="h-9 rounded-sm"
                    data-testid="storage-locations-search-btn"
                >
                    Suchen
                </Button>
                <Button
                    type="button"
                    onClick={printBulk}
                    variant="outline"
                    className="h-9 rounded-sm border-[#111827]"
                    disabled={selected.size === 0}
                    data-testid="storage-locations-bulk-print-btn"
                >
                    <FileDown className="w-4 h-4 mr-1" />
                    Etiketten drucken ({selected.size})
                </Button>
                <Button
                    type="button"
                    onClick={() => { setShowCreate(true); setEditingId(null); setForm(EMPTY_FORM); }}
                    className="h-9 rounded-sm bg-[#111827] hover:bg-black text-white"
                    data-testid="storage-locations-add-btn"
                >
                    <Plus className="w-4 h-4 mr-1" />
                    Neuer Lagerort
                </Button>
            </div>

            {/* Create / Edit Form */}
            {showCreate && (
                <div className="border border-gray-300 bg-white rounded-sm p-4 space-y-3" data-testid="storage-locations-form">
                    <div className="font-bold text-sm">
                        {editingId ? "Lagerort bearbeiten" : "Neuer Lagerort"}
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <div>
                            <Label className="text-[10px] font-bold uppercase tracking-wider text-gray-500">Code *</Label>
                            <Input
                                value={form.code}
                                onChange={(e) => setForm({ ...form, code: e.target.value })}
                                placeholder="H1-R3-F02"
                                className="rounded-sm mt-1 font-mono-num"
                                data-testid="storage-locations-form-code"
                            />
                        </div>
                        <div>
                            <Label className="text-[10px] font-bold uppercase tracking-wider text-gray-500">Anzeige-Name</Label>
                            <Input
                                value={form.name}
                                onChange={(e) => setForm({ ...form, name: e.target.value })}
                                placeholder="Halle 1 · Regal 3 · Fach 02"
                                className="rounded-sm mt-1"
                                data-testid="storage-locations-form-name"
                            />
                        </div>
                        <div>
                            <Label className="text-[10px] font-bold uppercase tracking-wider text-gray-500">Halle</Label>
                            <Input
                                value={form.hall}
                                onChange={(e) => setForm({ ...form, hall: e.target.value })}
                                placeholder="Halle 1"
                                className="rounded-sm mt-1"
                                data-testid="storage-locations-form-hall"
                            />
                        </div>
                        <div>
                            <Label className="text-[10px] font-bold uppercase tracking-wider text-gray-500">Regal</Label>
                            <Input
                                value={form.rack}
                                onChange={(e) => setForm({ ...form, rack: e.target.value })}
                                placeholder="Regal 3"
                                className="rounded-sm mt-1"
                                data-testid="storage-locations-form-rack"
                            />
                        </div>
                        <div>
                            <Label className="text-[10px] font-bold uppercase tracking-wider text-gray-500">Fach</Label>
                            <Input
                                value={form.shelf}
                                onChange={(e) => setForm({ ...form, shelf: e.target.value })}
                                placeholder="Fach 02"
                                className="rounded-sm mt-1"
                                data-testid="storage-locations-form-shelf"
                            />
                        </div>
                        <div className="sm:col-span-2">
                            <Label className="text-[10px] font-bold uppercase tracking-wider text-gray-500">Notiz</Label>
                            <Input
                                value={form.note}
                                onChange={(e) => setForm({ ...form, note: e.target.value })}
                                placeholder="z.B. Schwere Bauteile, schwer zugänglich"
                                className="rounded-sm mt-1"
                                data-testid="storage-locations-form-note"
                            />
                        </div>
                    </div>
                    <div className="flex items-center gap-2 justify-end">
                        <Button type="button" variant="outline" onClick={reset} disabled={busy} className="rounded-sm" data-testid="storage-locations-form-cancel">
                            Abbrechen
                        </Button>
                        <Button type="button" onClick={submit} disabled={busy} className="rounded-sm bg-[#111827] hover:bg-black text-white" data-testid="storage-locations-form-submit">
                            {busy ? "Speichert…" : (editingId ? "Speichern" : "Anlegen")}
                        </Button>
                    </div>
                </div>
            )}

            {/* List */}
            {loading && <div className="text-sm text-gray-500 py-4">Lade Lagerorte…</div>}

            {!loading && items.length === 0 && (
                <div className="bg-gray-50 border border-gray-200 rounded-sm py-8 text-center" data-testid="storage-locations-empty">
                    <MapPin className="w-8 h-8 mx-auto text-gray-300" />
                    <div className="mt-2 text-sm text-gray-500">Noch keine Lagerorte angelegt.</div>
                    <div className="text-xs text-gray-400 mt-1">Beginnen Sie mit „+ Neuer Lagerort".</div>
                </div>
            )}

            {!loading && items.length > 0 && (
                <div className="border border-gray-200 rounded-sm overflow-hidden">
                    <table className="w-full text-sm" data-testid="storage-locations-table">
                        <thead className="bg-gray-50 border-b border-gray-200">
                            <tr className="text-left">
                                <th className="px-3 py-2 w-10">
                                    <input
                                        type="checkbox"
                                        checked={selected.size === items.length && items.length > 0}
                                        onChange={selectAll}
                                        className="w-4 h-4 accent-[#111827]"
                                        data-testid="storage-locations-select-all"
                                    />
                                </th>
                                <th className="px-3 py-2 text-[10px] font-bold uppercase tracking-wider text-gray-500">Code</th>
                                <th className="px-3 py-2 text-[10px] font-bold uppercase tracking-wider text-gray-500">Halle · Regal · Fach</th>
                                <th className="px-3 py-2 text-[10px] font-bold uppercase tracking-wider text-gray-500">Name</th>
                                <th className="px-3 py-2 text-[10px] font-bold uppercase tracking-wider text-gray-500 text-right">Artikel</th>
                                <th className="px-3 py-2 text-[10px] font-bold uppercase tracking-wider text-gray-500 text-right">Aktionen</th>
                            </tr>
                        </thead>
                        <tbody>
                            {items.map((loc) => (
                                <tr key={loc.id} className="border-b border-gray-100 hover:bg-gray-50" data-testid={`storage-location-row-${loc.id}`}>
                                    <td className="px-3 py-2">
                                        <input
                                            type="checkbox"
                                            checked={selected.has(loc.id)}
                                            onChange={() => toggleSelect(loc.id)}
                                            className="w-4 h-4 accent-[#111827]"
                                            data-testid={`storage-location-select-${loc.id}`}
                                        />
                                    </td>
                                    <td className="px-3 py-2 font-mono-num font-bold text-[#111827]">{loc.code}</td>
                                    <td className="px-3 py-2 text-gray-600 text-xs">
                                        {[loc.hall, loc.rack, loc.shelf].filter(Boolean).join(" · ") || "—"}
                                    </td>
                                    <td className="px-3 py-2 text-gray-700">{loc.name || "—"}</td>
                                    <td className="px-3 py-2 text-right font-mono-num">{loc.article_count || 0}</td>
                                    <td className="px-3 py-2 text-right">
                                        <div className="inline-flex items-center gap-1">
                                            <button
                                                type="button"
                                                onClick={() => printSingle(loc)}
                                                className="p-1.5 hover:bg-gray-100 rounded-sm text-gray-600"
                                                title="QR-Etikett drucken"
                                                data-testid={`storage-location-print-${loc.id}`}
                                            >
                                                <Printer className="w-4 h-4" />
                                            </button>
                                            <button
                                                type="button"
                                                onClick={() => startEdit(loc)}
                                                className="p-1.5 hover:bg-gray-100 rounded-sm text-gray-600"
                                                title="Bearbeiten"
                                                data-testid={`storage-location-edit-${loc.id}`}
                                            >
                                                <Edit className="w-4 h-4" />
                                            </button>
                                            <button
                                                type="button"
                                                onClick={() => remove(loc)}
                                                className="p-1.5 hover:bg-red-50 rounded-sm text-red-600"
                                                title="Löschen"
                                                data-testid={`storage-location-delete-${loc.id}`}
                                            >
                                                <Trash2 className="w-4 h-4" />
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}
